import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  PublicFundDetailsType,
  PublicFundGeneralType,
} from '../../sj-orm/models/assets/categories';
import {
  FundUsageStrategyType,
  ManagementBodyType,
  PublicFundType,
} from '../../sj-orm/models/assets/enums';
import { ContactDto, ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type PublicFund = PublicFundFormGeneralType &
  PublicFundFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

const ManagementBodyList = [
  {
    label: 'forms_assets:myFund.enums.managementBody.manager',
    key: ManagementBodyType.manager,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.boardOfDirectors',
    key: ManagementBodyType.boardOfDirectors,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.managementCompany',
    key: ManagementBodyType.managementCompany,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.other',
    key: ManagementBodyType.other,
  },
];

export const PublicFundTypesList = [
  {
    label: 'forms_assets:publicFund.enums.fundType.VentureCapitalFund',
    key: PublicFundType.VentureCapitalFund,
  },
  {
    label: 'forms_assets:publicFund.enums.fundType.HedgeFund',
    key: PublicFundType.HedgeFund,
  },
  {
    label: 'forms_assets:publicFund.enums.fundType.PrivateFund',
    key: PublicFundType.PrivateFund,
  },
  {
    label: 'forms_assets:publicFund.enums.fundType.RealEstateFund',
    key: PublicFundType.RealEstateFund,
  },
  {
    label: 'forms_assets:publicFund.enums.fundType.IndexedFund',
    key: PublicFundType.IndexedFund,
  },
];

export const FundUsageStrategyList = [
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.strategic',
    key: FundUsageStrategyType.strategic,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.personal',
    key: FundUsageStrategyType.personal,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.investment',
    key: FundUsageStrategyType.investment,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.commercial',
    key: FundUsageStrategyType.commercial,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.other',
    key: FundUsageStrategyType.other,
  },
];

type PublicFundHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  fullLegalName: QuestionType.TEXT;
};

type PublicFundHelpDetailsType = {
  managementBody?: QuestionType.SELECT_FROM_LIST;
  manager?: QuestionType.PICK_ARRAY_OF_DTO;
  boardOfDirectors?: QuestionType.PICK_ARRAY_OF_DTO;
  managementCompany?: QuestionType.PICK_ARRAY_OF_DTO;
  other?: QuestionType.TEXT;
  fundFounders?: QuestionType.PICK_ARRAY_OF_DTO;
  typeofBusiness?: QuestionType.TEXT;
  legalEntityStructure?: QuestionType.TEXT;
  incorporationDate?: QuestionType.DATE_TIME;
  fundUsageStrategy?: QuestionType.SELECT_FROM_LIST;
  strategyDescription?: QuestionType.TEXT_MULTILINE;
  otherFundUsageStrategy?: QuestionType.TEXT;
  taxNumber?: QuestionType.TEXT;
  taxNumberIssuer?: QuestionType.TEXT;
  lei?: QuestionType.TEXT;
  address?: QuestionType.ADDRESS;
  province?: QuestionType.TEXT;
  zip?: QuestionType.TEXT;
  city?: QuestionType.TEXT;
  street?: QuestionType.TEXT;
  buildingNumber?: QuestionType.TEXT;
  apartments?: QuestionType.TEXT;
  otherAddressInfo?: QuestionType.TEXT_MULTILINE;
  ownershipProof?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type PublicFundFormGeneralType = TDtoWrapper<PublicFundGeneralType, PublicFundHelpGeneralType>;
type PublicFundFormDetailsType = TDtoWrapper<PublicFundDetailsType, PublicFundHelpDetailsType>;

const PublicFundFormGeneral: PublicFundFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:publicFund.inputs.type.label',
    tooltip: 'forms_assets:publicFund.inputs.type.tooltip',
    value: PublicFundType.HedgeFund,
    items: PublicFundTypesList,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:publicFund.inputs.investmentUnitInTheFund.label',
    tooltip: 'forms_assets:publicFund.inputs.investmentUnitInTheFund.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  fullLegalName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.fullLegalName.label',
    value: '',
    required: true,
  },
};

const PublicFundFormDetails: PublicFundFormDetailsType = {
  managementBody: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:publicFund.inputs.managementBody.label',
    items: ManagementBodyList,
    order: 1,
  },
  manager: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:publicFund.inputs.manager.label',
    colName: CollectionName.CONTACTS,
    showIf: {
      managementBody: {
        value: ManagementBodyType.manager,
      },
    },
    dtoType: [ContactType.INDIVIDUAL],
    filter: (contact) => (contact as unknown as ContactDto).type === ContactType.INDIVIDUAL,
    order: 2,
  },
  boardOfDirectors: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:publicFund.inputs.boardOfDirectors.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    showIf: {
      managementBody: {
        value: ManagementBodyType.boardOfDirectors,
      },
    },
    dtoType: [ContactType.INDIVIDUAL],
    filter: (contact) => (contact as unknown as ContactDto).type === ContactType.INDIVIDUAL,
    order: 3,
  },
  managementCompany: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:publicFund.inputs.managementCompany.label',
    colName: CollectionName.CONTACTS,
    showIf: {
      managementBody: {
        value: ManagementBodyType.managementCompany,
      },
    },
    dtoType: [ContactType.LEGAL_ENTITY],
    filter: (contact) => (contact as unknown as ContactDto).type === ContactType.LEGAL_ENTITY,
    order: 4,
  },
  other: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.other.label',
    showIf: {
      managementBody: {
        value: ManagementBodyType.other,
      },
    },
    order: 5,
  },
  fundFounders: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:publicFund.inputs.fundFounders.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    dtoType: [
      ContactType.LEGAL_ENTITY,
      ContactType.BANK,
      ContactType.BROKER,
      ContactType.INDIVIDUAL,
    ],
    order: 6,
  },
  typeofBusiness: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.typeofBusiness.label',
    order: 7,
  },
  legalEntityStructure: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.legalEntityStructure.label',
    order: 8,
  },
  incorporationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:publicFund.inputs.incorporationDate.label',
    tooltip: 'forms_assets:publicFund.inputs.incorporationDate.tooltip',
    order: 9,
  },
  fundUsageStrategy: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:publicFund.inputs.fundUsageStrategy.label',
    tooltip: 'forms_assets:publicFund.inputs.fundUsageStrategy.tooltip',
    items: FundUsageStrategyList,
    order: 10,
  },
  strategyDescription: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:publicFund.inputs.strategyDescription.label',
    order: 11,
  },
  otherFundUsageStrategy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.otherFundUsageStrategy.label',
    showIf: {
      fundUsageStrategy: {
        value: FundUsageStrategyType.other,
      },
    },
    order: 12,
  },
  taxNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.taxNumber.label',
    order: 13,
  },
  taxNumberIssuer: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.taxNumberIssuer.label',
    order: 14,
  },
  lei: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.lei.label',
    tooltip: 'forms_assets:common.inputs.lei.tooltip',
    order: 15,
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    order: 16,
  },
  legalEntityAddress: {
    type: FormLabelItem.label,
    label: 'forms_assets:common.inputs.legalEntityAddress.label',
    order: 16,
  },
  otherAddressInfo: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:publicFund.inputs.otherAddressInfo.label',
    order: 23,
  },
  attachments: baseAssetFields.attachments,
};

const PublicFundForm = [
  PublicFundFormGeneral,
  PublicFundFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'taxAgent',
    'taxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { PublicFundForm };
